import React from "react";
import { Alert, Button, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { TrackedStatus } from "./tracked-calibrations";
import { failureMessageKey } from "./failure-message";

interface CalibrationBannerAlertProps {
  status: TrackedStatus;
  fieldName: string;
  errorCode?: string;
  calibrationLink: string;
  onClose: () => void;
  onFollowLink: () => void;
}

export function CalibrationBannerAlert({
  status,
  fieldName,
  errorCode,
  calibrationLink,
  onClose,
  onFollowLink,
}: CalibrationBannerAlertProps) {
  const { t } = useTranslation();

  const closeButton = (
    <IconButton
      aria-label={t("button.close")}
      color="inherit"
      size="small"
      onClick={onClose}
    >
      <CloseIcon fontSize="inherit" />
    </IconButton>
  );

  const linkButton = (to: string, label: string) => (
    <Button
      component={Link}
      to={to}
      color="inherit"
      size="small"
      onClick={onFollowLink}
      sx={{
        color: "inherit",
        fontWeight: 600,
        "&:hover": {
          color: "inherit",
          backgroundColor:
            "color-mix(in srgb, var(--color-surface) 15%, transparent)",
        },
      }}
    >
      {label}
    </Button>
  );

  if (status === "succeeded") {
    return (
      <Alert severity="success" variant="filled" action={closeButton}>
        {t("message.calibration.banner.succeeded", { field: fieldName })}
      </Alert>
    );
  }

  if (status === "failed") {
    return (
      <Alert
        severity="error"
        variant="filled"
        action={
          <>
            {linkButton(calibrationLink, t("button.open_calibration"))}
            {closeButton}
          </>
        }
      >
        {`${t("message.calibration.banner.failed", { field: fieldName })} ${t(failureMessageKey(errorCode))}`}
      </Alert>
    );
  }

  if (status === "stalled" || status === "unknown") {
    return (
      <Alert severity="warning" variant="filled" action={closeButton}>
        {t(`message.calibration.banner.${status}`, { field: fieldName })}
      </Alert>
    );
  }

  return (
    <Alert
      severity="info"
      variant="filled"
      icon={<CircularProgress color="inherit" size={20} />}
      action={closeButton}
    >
      {t("message.calibration.banner.in_progress", { field: fieldName })}
    </Alert>
  );
}
